import { z } from 'zod';
import { emailSchema } from './auth.js';

/**
 * Student groups.
 *
 * A group is a visibility boundary and nothing more. Being a member lets you
 * see how your peers are doing — their name, XP, level, streak, how many
 * exercises they have solved. It does not expose their email, their queries,
 * their submissions, or anything they wrote.
 *
 * Note what `groupMemberSchema` does NOT contain: an email address. A coach
 * runs a cohort without harvesting contact details for it, and one learner
 * cannot read another's address off the leaderboard. Emails appear only in the
 * admin account screens, behind `requireAdmin`.
 */

export const groupSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  memberCount: z.number().int(),
  /** Display name only — the owner's email is not a member's business either. */
  ownerName: z.string(),
  ownerId: z.uuid(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  /** Whether the caller may rename it, archive it, or change its membership. */
  canManage: z.boolean(),
});
export type GroupSummary = z.infer<typeof groupSummarySchema>;

/** One line of a group leaderboard. */
export const groupMemberSchema = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  totalXp: z.number().int(),
  level: z.number().int(),
  exercisesSolved: z.number().int(),
  currentStreak: z.number().int(),
  lastActivityDate: z.string().nullable(),
  joinedAt: z.string(),
  /** Lets the UI highlight the viewer's own row without comparing ids itself. */
  isSelf: z.boolean(),
  isOwner: z.boolean(),
});
export type GroupMember = z.infer<typeof groupMemberSchema>;

export const groupDetailSchema = groupSummarySchema.extend({
  /** Ordered by XP, highest first — the leaderboard order. */
  members: z.array(groupMemberSchema),
});
export type GroupDetail = z.infer<typeof groupDetailSchema>;

export const groupListResponseSchema = z.object({
  items: z.array(groupSummarySchema),
  /** True when the caller may create a group at all (admin or coach). */
  canCreate: z.boolean(),
});
export type GroupListResponse = z.infer<typeof groupListResponseSchema>;

export const createGroupSchema = z.object({
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(500).nullish(),
});
export type CreateGroupRequest = z.infer<typeof createGroupSchema>;

export const updateGroupSchema = z
  .object({
    name: z.string().trim().min(2).max(80).optional(),
    description: z.string().trim().max(500).nullish(),
    archived: z.boolean().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, 'Nothing to update');
export type UpdateGroupRequest = z.infer<typeof updateGroupSchema>;

/**
 * Adding someone to a group.
 *
 * By **exact email**, deliberately, and the response says only whether it
 * worked. A coach is not an administrator and has no listing of the user base;
 * accepting a search term here would turn group management into a way to
 * enumerate every account on the platform a few letters at a time. Requiring
 * the full address means a coach can add someone they already know and learn
 * nothing about anyone they do not.
 */
export const addGroupMemberSchema = z.object({
  email: emailSchema,
});
export type AddGroupMemberRequest = z.infer<typeof addGroupMemberSchema>;
